import { CASP } from '@ui/src/components/transactions-sidepanels/types';
import {
  Account,
  Country,
  Currency,
  GeneralLedgerTransaction,
  Pagination,
  TransactionWarning,
} from '@ui/src';

export interface AccountLogs {
  action: string;
  comment: string;
  createdAt: string;
  id: string;
}

export enum NewApplicationStatus {
  INCOMPLETE = 'INCOMPLETE',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  READY_TO_REVIEW = 'READY_TO_REVIEW',
  REVIEW_IN_PROGRESS = 'REVIEW_IN_PROGRESS',
}

export enum CustomerType {
  INDIVIDUAL = 'individual',
  CORPORATE = 'corporate',
  NEW_APPLICATION = 'new-application',
}

export enum OrganizationType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
  COMPANY_OFFICIAL = 'COMPANY_OFFICIAL',
}

export enum NatureOfControlEnum {
  DIRECT_CONTROL = 'DIRECT_CONTROL',
  INDIRECT_CONTROL = 'INDIRECT_CONTROL',
}

export interface Comment {
  id?: string;
  user: string;
  createdDate: string;
  text?: string;
  document?: {
    link?: string;
    name: string;
  };
}

export enum CurrencyForWritingOffFee {
  IN_TRANSACTION_CURRENCY = 'in Transaction currency',
  IN_FEE_CURRENCY = 'in Fee currency',
}

export enum PspPartnerPaymentType {
  FASTER_PAYMENT = 'FASTER_PAYMENT',
  BACS = 'BACS',
  SEPA_SCT_INST = 'SEPA_SCT_INST',
  SEPA_SCT = 'SEPA_SCT',
  TARGET_2 = 'TARGET_2',
  SWIFT = 'SWIFT',
  FEDWIRE = 'FEDWIRE',
  CRYPTO_ASSET_PAYMENT = 'CRYPTO_ASSET_PAYMENT',
  CASH_OVER_COUNTER = 'CASH_OVER_COUNTER',
}

export interface FeeListItem {
  id?: string;
  name: string;
  value: string;
  type?: CurrencyForWritingOffFee;
}

export enum FeeSubTypeEnum {
  INACTIVITY = 'INACTIVITY',
  HIGH_BALANCE = 'HIGH_BALANCE',
  REGULAR = 'REGULAR',
}

export interface FeeTypeItem {
  title?: string;
  id?: string;
  list: FeeListItem[];
  type?: string;
  subType?: string;
}

export enum FeeTypeEnum {
  MANUAL = 'MANUAL',
  REGULAR = 'REGULAR',
  EXCHANGE = 'EXCHANGE',
  TRANSACTION = 'TRANSACTION',
}
export interface FeeType {
  name: string;
  feeType: string;
  feeAmountType: string;
  regularFeeSubType: string;
  regularFeePeriod: string;
  regularHighBalanceAmount: number;
  feePercentMinAmount: number;
  feePercentMaxAmount: number;
  paymentType: string;
  feeTargetAccountNumber: string;
  id: string;
  createdAt: string;
}

export interface SuspiciousTransaction {
  comments?: Comment[];
  canBeClosed?: boolean;
}

export interface CardsTransactions {
  id: string;
  date: string;
  description: string;
  transaction?: 'canceled' | 'good' | 'warning';
  currency: string;
  amount: string;
  merchantName: string;
  orderDetails: string;
  render?: Record<string, React.JSX.Element>;
}

export enum AccountTransactionType {
  INCOMING = 'Incoming',
  OUTGOING = 'Outgoing',
  INTERNAL = 'Internal',
  EXCHANGE = 'Exchange',
  GENERAL_LEDGER = 'General Ledger',
}

export interface AccountTransaction {
  id: string;
  date: string;
  description: AccountTransactionType;
  currency: string;
  debit: string;
  amount: string;
  balance: string;
  suspiciousTransaction?: boolean;
  render?: Record<string, React.JSX.Element>;
}

export interface StatementsContextType {
  accounts: Account[];
  cards: string[];
  currentAccount: Account;
  balances?: Balance[];
}

export interface AccountActivityLog {
  id: string;
  remoteUser: string;
  operationDate: string;
  typeOfOperation: string;
  ipAddress: string;
  typeOfDevice: string;
}

export interface CardTransaction {
  id: string;
  transaction?: string;
  description: string;
  currency: string;
  amountBc: string;
  amount: string;
  merchantName: string;
  terminalId: string;
  date: string;
}

export interface AmlComment {
  id: string;
  userName: string;
  text: string;
  date: string;
}

export enum RiskRate {
  GOOD = 'Good',
  MEDIUM = 'Medium',
  LOW = 'High',
}

export interface TotalRiskLevelValues {
  good: number[];
  medium: number[];
}

export interface GeneralMonthlyVolumesRiskScoring {
  totalRisk?: number;
  totalRiskScoreLevel?: RiskRate;
  comments?: AmlComment[];
  numberOfIncomingTransactions: {
    value: string;
    risk: number;
    realValue: string;
    warning?: string;
  };
  numberOfOutgoingTransactions: {
    value: string;
    risk: number;
    realValue: string;
    warning?: string;
  };
  valueOfIncomingTransactions: {
    value: string;
    risk: number;
    realValue: string;
    warning?: string;
  };
  valueOfOutgoingTransactions: {
    value: string;
    risk: number;
    realValue: string;
    warning?: string;
  };
  monthlyTurnover: {
    value: string;
    risk: number;
    realValue: string;
    warning?: string;
  };
}

export interface AmlLog {
  id: string;
  user: string;
  date: string;
  event: string;
  comment?: string;
}

export interface GeneralRiskScoring {
  totalRisk: {
    totalRiskScore?: number;
    totalRiskScoreLevel?: RiskRate;
    totalRiskScoreLogs: AmlLog[];
  };
}

export enum RiskScoringTypes {
  GEOGRAPHICAL_RISK = 'geographicalRisk',
  TYPE_OF_CLIENT = 'typeOfClient',
  DELIVERY_CHANNEL = 'deliveryChannel',
  PRODUCTS = 'products',
  MONTHLY_VOLUMES = 'monthlyVolumes',
  SOURCE_OF_FUNDS = 'sourceOfFunds',
  SIC_CODES = 'sicCodes',
  DATE_ON_INCORPORATION = 'dateOfIncorporation',
}

export interface MonthlyVolumes {
  numberOfIncomingTransactions?: {
    realValue: string;
    warning?: string;
  };
  numberOfOutgoingTransactions?: {
    realValue: string;
    warning?: string;
  };
  valueOfIncomingTransactions?: {
    realValue: string;
    warning?: string;
  };
  monthlyTurnover?: {
    realValue: string;
    warning?: string;
  };
  valueOfOutgoingTransactions?: {
    realValue: string;
    warning?: string;
  };
  maxAmountOfOnePayment?: {
    value: string;
    risk: number;
    realValue: string;
    warning?: string;
  };
  minAmountOfOnePayment?: {
    value: string;
    risk: number;
    realValue: string;
    warning?: string;
  };
}

export interface CommentValues {
  text: string;
  userName: string;
  type: RiskScoringTypes;
}

export enum CardPaymentSystem {
  VISA = 'Visa',
  MASTER_CARD = 'Mastercard',
  UNION_PAY = 'UnionPay',
}

export enum CardType {
  DEBIT = 'debit',
  CORP = 'corp',
}

export enum CardStatus {
  NEW = 'New',
  ORDERED = 'Ordered',
  APPROVED = 'Approved',
  ACTIVE = 'Active',
  FROZEN = 'Frozen',
  CANCELED = 'Canceled',
  VOID = 'Void',
}

export interface CustomerCard {
  id: string;
  paymentSystem: CardPaymentSystem;
  number: string;
  type: CardType;
  expiredDate: string;
  balance?: string;
  balanceCurrency: string;
  status: CardStatus;
  customerAccountId: string;
  address: string;
  customerFirstName: string;
  customerLastName: string;
  countryId?: string;
  virtual?: boolean;
  city?: string;
  postCode?: string;
  street?: string;
  apartment?: string;
  availableStatuses?: {
    name: string;
    value: CardStatus;
  }[];
}

export enum DocumentStatus {
  ACCEPTED = 'Accepted',
  NEW = 'New',
  EXPIRED = 'Expired',
  REJECTED = 'Rejected',
}

export interface DocumentComment {
  createdAt: string;
  deletedAt: string;
  documentId: string;
  id: string;
  text: string;
  updatedAt: string;
  userId: string;
  user: {
    profile: {
      firstName: string;
      lastName: string;
    };
  };
}

export interface Document {
  id: string;
  name: string;
  date: string;
  status: DocumentStatus;
  comments: number;
  commentsList?: DocumentComment[];
  link: string;
  warning?: boolean;
  isSelected?: boolean;
  documentType?: string;
  type: 'image' | 'file';
}

export enum DocumentType {
  IDENTIFICATION_DOCUMENTS = 'identificationDocuments',
  TRANSACTION_DOCUMENTS = 'transactionDocuments',
  PSP_CUSTOMER_DOCUMENTS = 'pspCustomerDocuments',
  CDD_CHECK_DOCUMENTS = 'cddCheckDocuments',
  OTHER_DOCUMENTS = 'otherDocuments',
}

export enum DocumentErrorType {
  EXPIRED = 'expired',
  EXPIRED_SOON = 'expiredSoon',
}

export interface DocumentInfo {
  id: string;
  link: string;
  group: DocumentType;
  status: DocumentStatus;
  expiredDate: string;
  name: string;
  loadedDate: string;
  userName: string;
  error?: {
    type: DocumentErrorType;
    title: string;
    description?: string;
  };
  message?: string;
  comments?: DocumentComment[];
}

export enum WriteOffType {
  PERCENT_OF_BALANCE = 'Percent of balance',
  FIXED_VALUE = 'Fixed value',
}

export enum WriteOffFeeCurrency {
  IN_FEE_CURRENCY = 'In Fee currency',
  IN_TRANSACTION_CURRENCY = 'In Transaction currency',
}

export enum FeeStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  ARCHIVE = 'Archive',
}

export enum TariffLogTypeEnum {
  CHANGED_TARIFF = 'CHANGED_TARIFF',
  CHANGED_WRITE_OFF_TYPE = 'CHANGED_WRITE_OFF_TYPE',
  SET_WRITE_OFF_VALUE = 'SET_WRITE_OFF_VALUE',
  CHANGED_WRITE_OFF_VALUE = 'CHANGED_WRITE_OFF_VALUE',
}

export interface FeeLog {
  id: string;
  type: TariffLogTypeEnum;
  from?: string;
  to?: string;
  value?: string;
  isModified?: boolean;
  user: {
    firstName: string;
    lastName: string;
  };
  date: string;
}

export enum FeeAmountTypeEnum {
  FIXED = 'FIXED',
  PERCENT = 'PERCENT',
}

export interface FeeItemFeeType {
  id: string;
  feeTargetAccountNumber: string;
  feeType: string;
  name: string;
}

export interface FeeItem {
  feeAmountType: string;
  feeFixedAmount: number;
  feeId: string;
  feePercentAmount: number;
  feePercentMaxAmount: number;
  feePercentMinAmount: number;
  feeTargetAccountNumber: string;
  feeCascadeCurrenciesIds?: string[];
  feeCurrencyId?: string;
  id: string;
  name: string;
  tariffId: string;
  fee: FeeItemFeeType;
  updatedAt: string;
  currency?: string;
  writeOffValue?: string;
  isModified?: boolean;
  url?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface TariffsLog {
  id: string;
  type: TariffLogTypeEnum;
  tariff?: {
    id: string;
    name: string;
  };
  fee?: {
    id: string;
    name: string;
  };
  user: {
    firstName: string;
    lastName: string;
  };
  value?: string;
  from?: string;
  to?: string;
  date: string;
}

export interface Transaction {
  date: string;
  amount: string;
  currency: string;
  debitAccount: string;
  creditAccount: string;
  info?: string;
}

export interface Tariff {
  name: string;
  riskScoreRangeId: string;
  id: string;
}

export interface CustomFeeFormValues {
  feeId: string;
  name: string;
  feeTargetAccountNumber?: string;
  feePercentAmount?: number;
  feeFixedAmount?: number;
  feePercentMinAmount?: number;
  feePercentMaxAmount?: number;
  feeAmountType: string;
  feeType?: string;
  feeCascadeCurrenciesIds?: string[];
  feeCurrencyId?: string;
}

export interface AgreementItem {
  id: string;
  type: string;
  version: string;
  dateOfSigning?: string;
  link: string;
  earlyVersions?: {
    id: string;
    type: string;
    version: string;
    dateOfSigning?: string;
    link: string;
  }[];
}

export enum EmploymentStatus {
  EMPLOYEE = 'EMPLOYEE',
  SELF_EMPLOYED = 'SELF_EMPLOYED',
  STUDENT = 'STUDENT',
  RETIREE = 'RETIREE',
  UNEMPLOYED = 'UNEMPLOYED',
  OTHER = 'OTHER',
}

export enum CustomerStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  DISABLED = 'DISABLED',
  CLOSED = 'CLOSED',
}

export interface BeneficiaryContentRowType {
  name: string;
  value: string;
  error?: string;
}

export enum ClientScoreTypeEnum {
  AUTO = 'AUTO',
  MANUAL = 'MANUAL',
}

export interface CustomerScore {
  id: string;
  createdAt: string;
  updatedAt: string;
  organizationId: string;
  applicationId: string;
  totalRiskScore: number;
  geographicalRiskTotal: number;
  countryRisk: number;
  countryId: string;
  nationalityRisk: number;
  nationalityId: string;
  deliveryChannelRisk: number;
  deliveryChannelId: string;
  clientActivityRisk: number;
  clientActivityId: string;
  countryOfIncorporationRisk?: number;
  countryOfIncorporationId?: string;
  sourcesOfFundsRiskTotal: number;
  sourcesOfFundsRiskItems: { id: string; name: string; value: number }[];
  productsRiskTotal: number;
  productsRiskItems: { id: string; name: string; value: number }[];
  sicRiskTotal: number;
  sicRiskItems: { id: string; name: string; value: number }[];
  officialRiskItems: { id: string; name: string; value: string }[];
  officialRiskTotal: number;
  monthlyTurnoverRangeRisk: number;
  monthlyTurnoverRangeId: string;
  monthlyTransactionRangeRisk: number;
  monthlyTransactionRangeId: string;
  dateOfIncorporationRisk: number;
  scoreCalculatedAt: string;
  scoreAcceptedAt: string;
  country: number;
  nationality: { id: string; name: string; value: number };
  deliveryChannel: string;
  clientActivity: string;
  monthlyTurnoverRange: string;
  monthlyTransactionRange: { id: string; name: string; value: number };
  dateOfIncorporation: string;
  clientScoreType: ClientScoreTypeEnum;
  employeeId?: string;
  order?: number;
}

export interface CustomerRanges {
  monthlyMaxValueIncomingPaymentRangeId?: string;
  monthlyMaxValueOutgoingPaymentRangeId?: string;
  monthlyNumberIncomingTransactionsRangeId?: string;
  monthlyNumberOutgoingTransactionsRangeId?: string;
  monthlyTurnoverRangeId?: string;
  monthlyValueIncomingTransactionsRangeId?: string;
  monthlyValueOutgoingTransactionsRangeId?: string;
}

export interface SupplierCustomersProps {
  applicationId: string;
  monthlyTurnoverRangeId: string;
  name: string;
  id: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface Company {
  authLevel: string;
  companyOfficialType: string;
  createdAt: string;
  id: string;
  rejectedAt: string;
  archivedAt: string;
  archivedBy: string;
  rejectedBy: string;
  approvedBy: string;
  approvedAt: string;
  rejectionReason?: string;
  natureOfControl: NatureOfControlEnum;
  organizationId: string;
  signatureType: SignatureType;
  accessRight?: string;
  percentage: string;
  position: string;
  status: string;
  havingControl?: boolean;
  employeeName?: string;
  user?: {
    profile?: {
      firstName: string;
      lastName: string;
    };
  };
  organization: {
    companyName: string;
    id: string;
  };
  render?: Record<string, React.JSX.Element>;
}

export enum CofStatus {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  ARCHIVED = 'ARCHIVED',
  PENDING_REVIEW = 'PENDING_REVIEW',
}

export enum SignatureType {
  NONE = 'NONE',
  SINGLE = 'SINGLE',
}

export enum AccessRight {
  NONE = 'NONE',
  VIEWER = 'VIEWER',
}

export interface CofProps {
  userId: string;
  organizationId: string;
  status: CofStatus;
  position: string;
  percentage: string;
  natureOfControl: NatureOfControlEnum;
  companyOfficialType: string;
  signatureType: SignatureType;
  accessRight: AccessRight;
  rejectionReason: string;
  archivedAt: string;
  rejectedAt: string;
  approvedAt: string;
  archivedBy: string;
  rejectedBy: string;
  approvedBy: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  havingControl?: boolean;
  officialApplication?: {
    id: string;
    status: NewApplicationStatus;
    type: string;
    firstName: string;
    lastName: string;
  };
  user: {
    status: string;
    totpEnabled: string;
    serviceRole: string[];
    organization?: {
      id?: string;
    };
    profile: {
      firstName: string;
      lastName: string;
      dob: string;
      id: string;
      createdAt: string;
      updatedAt: string;
      deletedAt: string;
    };
  };
}

export interface CustomerProps {
  accounts: {
    edges: {
      node: Account & { visibleToClient: boolean; statusLogs: AccountLogs[] };
    }[];
  };
  application?: {
    id: string;
  };
  ids?: string;
  url?: string;
  search?: string;
  address1: string;
  address2: string;
  applicationId: string;
  statusChangeReason?: string;
  businessAddress1: string;
  businessAddress2: string;
  businessAddressSameAsRegisteredAddress: boolean;
  businessCity: string;
  businessCountryId: string;
  businessDescription: string;
  businessLicense: string;
  businessPostalCode: string;
  city: string;
  clientActivityId: string;
  typeOfActivityDescription?: string;
  clientScheme?: {
    id: string;
    name: string;
  };
  clientsScoreResult: CustomerScore;
  customers?: SupplierCustomersProps[];
  suppliers?: SupplierCustomersProps[];
  companyName: string;
  companyNumber: string;
  correspondenceAddress1: string;
  correspondenceAddress2: string;
  correspondenceAddressSameAsRegisteredAddress: boolean;
  correspondenceCity: string;
  correspondenceCountryId: string;
  correspondencePostalCode: string;
  countriesOfInterestIds: string[];
  idType?: string;
  idNumber?: string;
  idExpDate?: string;
  merchantId?: string;
  country: {
    id: string;
    name: string;
    riskScore: number;
  };
  customFees?: FeeItem[];
  countryId: string;
  countryOfIncorporationId: string;
  dateOfIncorporation: string;
  deliveryChannelId: string;
  employmentStatus: EmploymentStatus;
  firstName: string;
  id: string;
  isUSResident: boolean;
  lastName: string;
  mccCode: string;
  monthlyMaxValueIncomingPaymentRangeId: string;
  monthlyMaxValueOutgoingPaymentRangeId: string;
  monthlyNumberIncomingTransactionsRangeId: string;
  monthlyNumberOutgoingTransactionsRangeId: string;
  monthlyTurnoverRangeId: string;
  monthlyValueIncomingTransactionsRangeId: string;
  monthlyValueOutgoingTransactionsRangeId: string;
  nationalityId: string;
  operationStatus: string;
  organizationFeatures: string;
  participatesInPolitics: boolean;
  postalCode: string;
  registeredAddress1: string;
  registeredAddress2: string;
  registeredCity: string;
  registeredCountryId: string;
  registeredPostalCode: string;
  riskProfile: { totalRiskScore: number };
  sicCodes: string[];
  questionnaire?: Questionnarie;
  vatNumber?: string;
  taxNumber?: string;
  amlCtfComplianceProgram?: boolean | string | null;
  requiredCurrenciesCryptoId?: string[];
  purposeOfAccount?: string;
  expectedTypeOfClients?: string[];
  pspIntegrationOrganization?: ExternalOrganization;
  birthCountryId?: string;
  placeOfBirth?: string;
  nameOfEmployer?: string;
  employeePosition?: string;
  countryOfEmployerId?: string;
  officials?: {
    edges: {
      node: {
        authLevel: string;
        companyOfficialType: string;
        createdAt: string;
        deletedAt: string;
        id: string;
        natureOfControl: NatureOfControlEnum;
        organizationId: string;
        percentage: string;
        position: string;
        status: string;
        user: {
          status: string;
          profile: {
            firstName: string;
            lastName: string;
            dob: string;
          };
        };
        userId: string;
      };
    }[];
  };
  sourcesOfFunds: {
    edges: {
      node: {
        riskScoreRange: {
          name: string;
          rangeStart: number;
          rangeEnd: number;
          action: string;
          id: string;
        };
        name: string;
        riskScoreRangeId: string;
        riskScore: number;
        id: string;
      };
    }[];
  };
  products: {
    edges: {
      node: {
        name: string;
        id: string;
      };
    }[];
  };
  status: CustomerStatus;
  tariffId: string;
  tariff: {
    name: string;
    type: string;
    status: string;
    isActive: boolean;
    isDefault: boolean;
    riskScoreRangeId: string;
    fees: FeeItem[];
    id: string;
    createdAt: string;
    updatedAt: string;
  };
  type: OrganizationType;
  user: {
    id: string;
    totpEnabled?: boolean;
    profile?: {
      dob: string;
      additionalEmail?: string;
    };
    email: string;
    phone: string;
  };
  userId: string;
  dob?: string;
  websiteLink: string;
  representativeOfOrganizations: Company[];
  render?: Record<string, React.JSX.Element>;
}

export enum CustomerScoreUpdateStatus {
  QUEUED = 'QUEUED',
  FAIL = 'FAIL',
  SUCCESS = 'SUCCESS',
}

export interface CustomerScoreQueuedProps {
  id: string;
  organizationId: string;
  status: CustomerScoreUpdateStatus;
}

export enum TransactionTypeEnum {
  INTERNAL_PAYMENT = 'INTERNAL_PAYMENT',
  CURRENCY_EXCHANGE = 'CURRENCY_EXCHANGE',
  NOSTRO = 'NOSTRO',
  GENERAL_LEDGER = 'GENERAL_LEDGER',
  INTERNAL = 'INTERNAL',
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
  FEE = 'FEE',
}

export interface TransactionTypeProps {
  active: boolean;
  createdAt: string;
  id: string;
  name: TransactionTypeEnum;
}

export interface StatementListItemProps {
  id: string;
  currencyId: string;
  sum: number;
  type: TransactionTypeEnum;
  transferDate?: string;
  counterpartyAccountNumber?: string;
  counterpartyFullName?: string;
  counterpartyAccountName?: string;
  counterpartyFirstName?: string;
  counterpartyLastName?: string;
  documentNumber?: string;
  currency?: Currency;
  generalLedgerTransactionId?: string;
  transactionId?: string;
  creditSum?: number;
  debitSum?: number;
  balance?: number;
  url?: string;
  render?: Record<string, React.JSX.Element>;
}

export type StatementListItem = GeneralLedgerTransaction & {
  type: string;
  typeId: string;
};

export enum AmlAction {
  AML_REVIEW = 'AML_REVIEW',
  OPS_REVIEW = 'OPS_REVIEW',
  NONE = 'NONE',
}

export interface RiskScoreRange {
  name: string;
  rangeStart: number;
  rangeEnd: number;
  id: string;
  action: AmlAction;
}

export interface Balance {
  accountId: string;
  currencyId: string;
  currency?: { name: string };
  id: string;
  value: number;
  overdraftEnabled?: boolean;
  overdraftValue?: number;
  currencyName?: string;
}

export interface OrderCardFormValues {
  userId: string;
  physical?: boolean;
  cardType?: string;
  paymentSystem: string;
  countryId?: string;
  city?: string;
  postCode?: string;
  street?: string;
  apartment?: string;
  countryName?: string;
}

export enum CustomerDocumentType {
  APPLICATION_INCORPORATION_CERTIFICATE = 'APPLICATION_INCORPORATION_CERTIFICATE',
  APPLICATION_ARTICLES_OF_ASSOCIATIONS = 'APPLICATION_ARTICLES_OF_ASSOCIATIONS',
  APPLICATION_REGISTRY_OF_THE_SHAREHOLDERS = 'APPLICATION_REGISTRY_OF_THE_SHAREHOLDERS',
  APPLICATION_REGISTRY_OF_THE_DIRECTORS = 'APPLICATION_REGISTRY_OF_THE_DIRECTORS',
  APPLICATION_ANNUAL_ACCOUNTS = 'APPLICATION_ANNUAL_ACCOUNTS',
  CLIENT_IDENTIFICATION = 'CLIENT_IDENTIFICATION',
  CLIENT_TRANSACTION = 'CLIENT_TRANSACTION',
  CLIENT_PSP_CUSTOMER = 'CLIENT_PSP_CUSTOMER',
  CLIENT_CDD_CHECK = 'CLIENT_CDD_CHECK',
  OTHER = 'OTHER',
}

export enum CustomerDocumentStatus {
  NEW = 'NEW',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

export interface CustomerDocument {
  name: string;
  uploadId: string;
  category?: CustomerDocumentType;
  expirationDate: string;
  status?: CustomerDocumentStatus;
  userId: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  isSelected?: boolean;
  commentsList?: DocumentComment[];
  documentCategoryId?: string;
  prevDocumentCategoryId?: string;
  transactionId?: string;
  url?: string;
  upload?: {
    mimeType?: string;
  };
  countComments?: {
    count: number;
  };
  comments?: {
    edges: {
      node: DocumentComment;
    }[];
  };
  warning?: boolean;
  warningMessage?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface BaseStatistics {
  typeId: string;
  count: number;
  avgValue: number;
  minValue: number;
  maxValue: number;
  sum: number;
}

export interface ProfitByCustomer {
  feesReceived: number;
  turnover: number;
  profitPercent: number;
}
export interface CustomerStats extends ProfitByCustomer {
  stats: BaseStatistics[];
}

export interface PspPartner {
  id: string;
  companyName: string;
  description?: string;
  pspIntegrationId?: string;
}

export interface TopUpVisibility {
  organizationId: string;
  id: string;
  visible: boolean;
  pspPartnerId: string;
}

export interface TopUpInstruction {
  beneficiary: string;
  beneficiaryAddress: string;
  beneficiaryAccountNumber: string[];
  beneficiaryBankName: string;
  beneficiaryBankBIC: string;
  purposeOfPayment: string;
  paymentType: string;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
  beneficiaryBankABA?: string;
  accountOfCorrespondentWithIntermediaryBank?: string;
  casp?: {
    id: string;
    name: string;
  };
}

export interface TopUpInstructions {
  items: TopUpInstruction[];
}

export enum AgreementType {
  GENERAL_T_AND_CS = 'GENERAL_T_AND_CS',
  PRIVACY_NOTICE = 'PRIVACY_NOTICE',
  ANNEX_1 = 'ANNEX_1',
  ANNEX_2 = 'ANNEX_2',
  ANNEX_3 = 'ANNEX_3',
  ANNEX_4 = 'ANNEX_4',
  ANNEX_5 = 'ANNEX_5',
  ANNEX_6 = 'ANNEX_6',
  ANNEX_7 = 'ANNEX_7',
  ANNEX_8 = 'ANNEX_8',
}

export interface CustomerAgreement {
  archivedAt: string;
  client: CustomerType;
  createdAt: string;
  description: string;
  id: string;
  signatures: {
    edges: { node: { id: string; signedAt: string } }[];
  };
  startAt: string;
  type: AgreementType;
  uploadId: string;
  version: number;
  earlyVersions?: {
    archivedAt: string;
    client: CustomerType;
    createdAt: string;
    description: string;
    id: string;
    signatures: {
      edges: { node: { id: string; signedAt: string } }[];
    };
    startAt: string;
    type: AgreementType;
    uploadId: string;
    version: number;
  }[];
  render?: Record<string, React.JSX.Element>;
}

export type CustomerAgreementDictionary<T> = Record<string, T[]>;

export interface NewCofProps {
  userId?: string;
  email: string;
  dob?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  organizationId?: string;
  position?: string;
  percentage?: string;
  natureOfControl?: NatureOfControlEnum;
  companyOfficialType?: string;
  signatureType?: SignatureType;
  havingControl?: boolean;
  accessRight?: AccessRight;
}

export interface ChatBody {
  title?: string;
  userId?: string;
  employeeId: string;
  organizationId: string;
}

export interface ProblemTransactionCofProps {
  userId: string;
  organizationId: string;
  status: string;
  position: string;
  companyOfficialType: string;
  id: string;
  user: {
    status: string;
    totEnabled: boolean;
    serviceRole: string[];
    organization?: {
      id?: string;
    };
    profile: {
      firstName: string;
      lastName: string;
      dob: string;
      id: string;
      createdAt: string;
      updatedAt: string;
      deletedAt: string;
    };
  };
}

export interface UploadResponse {
  userId: string;
  mimeType: string;
  size: number;
  id: string;
  createdAt: string;
  name: string;
}

export enum IdType {
  ID_CARD = 'ID_CARD',
  PASSPORT = 'PASSPORT',
  DRIVERS = 'DRIVERS',
  RESIDENCE_PERMIT = 'RESIDENCE_PERMIT',
  UTILITY_BILL = 'UTILITY_BILL',
  SELFIE = 'SELFIE',
  VIDEO_SELFIE = 'VIDEO_SELFIE',
  PROFILE_IMAGE = 'PROFILE_IMAGE',
  ID_DOC_PHOTO = 'ID_DOC_PHOTO',
  AGREEMENT = 'AGREEMENT',
  CONTRACT = 'CONTRACT',
  DRIVERS_TRANSLATION = 'DRIVERS_TRANSLATION',
  INVESTOR_DOC = 'INVESTOR_DOC',
  VEHICLE_REGISTRATION_CERTIFICATE = 'VEHICLE_REGISTRATION_CERTIFICATE',
  INCOME_SOURCE = 'INCOME_SOURCE',
  PAYMENT_METHOD = 'PAYMENT_METHOD',
  BANK_CARD = 'BANK_CARD',
  COVID_VACCINATION_FORM = 'COVID_VACCINATION_FORM',
  OTHER = 'OTHER',
}

export interface OngoingMonitring {
  createdAt: string;
  id: string;
  organizationId: string;
  updatedAt: string;
  blocklistNumber?: string;
  warnings: {
    edges: { node: TransactionWarning }[];
    pageInfo: Pagination;
  };
  warningsGroupsBlacklists: {
    edges: {
      node: {
        blacklist: {
          name: string;
        };
        blacklistId: string;
        createdAt: string;
        deletedAt: string;
        id: string;
        updatedAt: string;
        warningGroupId: string;
        warningsCount: string;
      };
    }[];
    pageInfo: Pagination;
  };
  render?: Record<string, React.JSX.Element>;
}

export enum OngoingStatus {
  QUEUED = 'QUEUED',
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
}

export interface OngoingCheckingStatus {
  organizationId?: string;
  applicationId?: string;
  status: OngoingStatus;
  id: string;
  createdAt: string;
}

export enum BeneficiaryStatus {
  DRAFT = 'DRAFT',
  UNDER_REVIEW = 'UNDER_REVIEW',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
}

export interface Beneficiary {
  address: string;
  aba?: string;
  bankAccountNumber: null;
  bankAddress: string;
  bankCountry: string;
  bankName: string;
  collectorContact?: string;
  beneficiaryType: OrganizationType;
  bic: string;
  city: string;
  correspondentAccount: string;
  country: {
    name: string;
  };
  countryId: string;
  currency: {
    name: string;
  };
  currencyId: string;
  iban: string;
  id: string;
  intermediaryBankAddress: string;
  intermediaryBankBIC: string;
  intermediaryBankCorrespondentAccount: string;
  intermediaryBankCountry: string;
  intermediaryBankName: string;
  name: string;
  nickname: string;
  organizationId: string;
  organization?: CustomerProps;
  paymentType: string;
  phone: string;
  postCode: string;
  sortCode: string;
  status: BeneficiaryStatus;
  useIntermediaryBank: boolean;
  countContracts: {
    count: number;
  };
  documents?: {
    edges: {
      node: {
        id: string;
        documentCategoryId: string;
        name: string;
        uploadId: string;
      };
    }[];
  };
  render?: Record<string, React.JSX.Element>;
}

export interface BeneficiaryStatusLogs {
  id: string;
  beneficiaryId: string;
  createdAt: string;
  deletedAt: string;
  diff: {
    next: {
      status: BeneficiaryStatus;
      updatedAt: string;
    };
    prev: {
      status: BeneficiaryStatus;
      updatedAt: string;
    };
  };
  updatedAt: string;
  updatedByEmployee: {
    profile: {
      firstName: string;
      lastName: string;
    };
  };
}

export interface ContractStatement {
  id: string;
  createdAt: string;
  currencyId: string;
  currency: {
    name: string;
    decimals: number;
  };
  sum: number;
  creditSum: number;
  debitSum: number;
  generalLedgerTransaction?: {
    creditAccountNumber: string;
    debitAccountNumber: string;
  };
  type: string;
  transactionId: string;
  operationType: string;
  render?: Record<string, React.JSX.Element>;
}

export interface BeneficiaryContract {
  beneficiaryId: string;
  createdAt: string;
  deletedAt: string;
  currencyId?: string;
  id: string;
  name: string;
  updatedAt: string;
  sum?: number;
  description?: string;
  status: string;
  transactionsSum?: number;
  statementItems?: {
    edges: {
      node: ContractStatement;
    }[];
  };
  supportingDocuments?: {
    edges: {
      node: {
        id: string;
        uploadId: string;
        createdAt: string;
        name?: string;
      };
    }[];
  };
  render?: Record<string, React.JSX.Element>;
}

export interface BeneficiaryContractFormValues {
  id: string;
  name: string;
  currencyId?: string;
  description?: string;
  sum?: number;
  status?: string;
  uploadId?: string[];
}

export enum ApplicationSchemeType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
  COMPANY_OFFICIAL = 'COMPANY_OFFICIAL',
}

export enum ClientType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
}

export interface ClientScheme {
  name: string;
  primaryId?: string;
  transactionRiskScoringEnabled: boolean;
  transactionMonitoringEnabled: boolean;
  transactionBlacklistEnabled: boolean;
  transactionAgreementEnabled: boolean;
  currencies: {
    edges: any[];
  };
  id: string;
}

export interface ApplicationScheme {
  name: string;
  type: ApplicationSchemeType;
  active: boolean;
  visible: boolean;
  deliveryChannelVisible?: boolean;
  deliveryChannelTitle?: string;
  deliveryChannelId?: string;
  countriesOfInterestActive?: boolean;
  countriesOfInterestTitle?: string;
  clientSchemeId?: string;
  deliveryChannel: {
    name: string;
    active: boolean;
    riskScoreRangeId: string;
    riskScore: number;
    clientType: ClientType;
    id: string;
  };
  kycProvider: string;
  kycRequired: boolean;
  agreementsActive: boolean;
  agreementsRequired: boolean;
  taxNumberActive: boolean;
  taxNumberRequired: boolean;
  vatNumberActive: boolean;
  vatNumberRequired: boolean;
  accountantDetailsActive?: boolean;
  auditedByExternalComplianceActive?: boolean;
  changesInManagementOrOwnershipActive?: boolean;
  complianceConsultantDetailsActive?: boolean;
  customersEnabled?: boolean;
  fcaNumberActive?: boolean;
  hmrcMlrNumberAndExpiryDateActive?: boolean;
  icoNumberActive?: boolean;
  itProviderDetailsActive?: boolean;
  mlroDetailsActive?: boolean;
  numberOfAgentsActive?: boolean;
  numberOfBranchesActive?: boolean;
  onboardCorporateClientsActive?: boolean;
  onboardMsbActive?: boolean;
  paymentsForCharitiesActive?: boolean;
  payoutPartnerDetailsActive?: boolean;
  hmrcMlrNumberActive?: true;
  hmrcMlrNumberRequired?: true;
  hmrcRegistrationExpiryDateActive?: true;
  hmrcRegistrationExpiryDateRequired?: true;
  regulatoryInvestigationActive?: boolean;
  screeningActive?: boolean;
  suppliersEnabled?: boolean;
  transactionMonitoringActive?: boolean;
  typeOfBusinessLicenceActive?: boolean;
  visitedByAuthoritiesActive?: boolean;
  workingWithAnyOtherIPSPActive?: boolean;
  civilActive?: boolean;
  criminalOfAnyActive?: boolean;
  lastThreeYearsAddressHistoryActive?: boolean;
  nationalInsuranceNumberOfDirectorsActive?: boolean;
  regulatoryActive?: boolean;
  businessAndEmploymentActive?: boolean;
  clientScheme: ClientScheme;
  amlCtfComplianceProgramActive?: boolean;
  amlCtfComplianceProgramRequired?: boolean;
  expectedTypeOfClientsActive?: boolean;
  expectedTypeOfClientsRequired?: boolean;
  purposeOfAccountActive?: boolean;
  purposeOfAccountRequired?: boolean;
  requiredCurrenciesCryptoActive?: boolean;
  requiredCurrenciesCryptoRequired?: boolean;
  customersNameTitle?: string;
  customersTitle?: string;
  suppliersAndCustomersTitle?: string;
  suppliersNameTitle?: string;
  suppliersTitle?: string;
  documentsCategories: {
    edges: {
      node: {
        createdAt: string;
        deletedAt: string;
        id: string;
        name: string;
        required: boolean;
        updatedAt: string;
      };
    }[];
  };
  id: string;
  render?: Record<string, React.JSX.Element>;
}

export interface COFAndIndividualQuestionnarieFormValues {
  nationalInsuranceNumberOfDirectors?: string;
  lastThreeYearsAddressHistory?: string;
  criminalConvictedOfAny?: string | boolean;
  criminalReceivedACautionForAny?: string | boolean;
  criminalArrestedOrChargedWithOrBeenInvolved?: string | boolean;
  criminalSubjectToAnyCurrentCriminalInvestigation?: string | boolean;
  criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted?:
    string | boolean;
  criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?:
    string | boolean;
  criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved?:
    string | boolean;
  civilFoundLiableForFraud?: string | boolean;
  civilSubjectToAJudgmentDebt?: string | boolean;
  civilInvolvedInAnyCivilProceedings?: string | boolean;
  civilYouHeldAPositionOfInfluence?: string | boolean;
  civilFiledForBankruptcy?: string | boolean;
  civilSubjectToABankruptcy?: string | boolean;
  businessAndEmploymentDismissedOrAskedTo?: string | boolean;
  businessAndEmploymentDisqualifiedFromActingAsADirector?: string | boolean;
  businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings?:
    string | boolean;
  regulatoryAnAuthorizationFromARegulatoryBodyRefused?: string | boolean;
  regulatoryCensured?: string | boolean;
  regulatoryInvolvedInAnyActivitiesRequiringAuthorization?: string | boolean;
  regulatorySubjectToAnyDisqualification?: string | boolean;
  otherNationalitiesIds?: string[];
  maritalStatus?: string;
  spouseFullName?: string;
  natureOfBusinessActivity?: string;
  nameOfCompany?: string;
  ownership?: number | string;
  belongsToGroup?: boolean | string;
  parentCompanyName?: string;
  parentCompanyCountryId?: string;
  subsidiariesInfo?: string;
  mainActivities?: string;
  groupWealthSourceIds?: string[];
  estimatedTotalWealthEuro?: string | number;
  requiresSourcesOfIncomes?: string | boolean;
  sourcesOfIncomes?:
    | {
        id?: string;
        sourceId: string;
        value: string | number;
      }[]
    | null;
  sourcesOfIncomesOther?: string;
  sourcesOfIncomesBorrowedOther?: string;
  requiresSourceAndSizeOfWealth?: string | boolean;
  sourcesAndSizesOfWealth?:
    | {
        id?: string;
        sourceId: string;
        value: string | number;
      }[]
    | null;
  sourcesAndSizesOfWealthOther?: string;
  sourcesAndSizesOfWealthBorrowedOther?: string;
  requiresNetWorth?: string | boolean;
  netWorthAmount?: string | number | null;
  questionnairesAccountsCurrencies?: string;
  questionnairesAccountsFlows?: string;
  questionnairesExpectedFundsOrigins?: string;
  questionnairesAffiliatedCompanies?: string;
  sourcesOfFundsIds?: string[];
  sourcesOfFundsOther?: string;
  sourcesOfFundsLoanCreditInstitution?: string;
  sourcesOfFundsLoanBusinessPartner?: string;
  hasAccountsInOtherFinancialInstitutions?: string | boolean;
  otherFinancialInstitutions?: string;
  groupWealthSourceOther?: string;
  secondPassportExpiryDate?: string;
  secondPassportIssueCountryId?: string;
  secondPassportIssueDate?: string;
  secondPassportNumber?: string;
  isPepOrAssociate?: string | boolean;
  nameOfPep?: string;
  relToPep?: string;
  orgEmployerAndPosition?: string;
  positionHeldCountryId?: string;
  politicalFunctionPeriodFrom?: string;
  politicalFunctionPeriodTo?: string;
  abilityMoveGovernmentFunds?: boolean | string;
  abilityMoveGovernmentFundsDetails?: string;
  isUSCitizen?: string | boolean;
  questionnairesCrsFatcaDeclarations?: string;
  homePhoneNumber?: string;
  emails?: string[];
  accountsCurrencies?: string;
  accountsFlows?: string;
  hasAffiliatedCompanies?: string | boolean;
}

export interface CorporateQuestionnarieFormValues {
  typeOfBusinessLicence?: string;
  hmrcMlrNumberAndExpiryDate?: string;
  fcaNumber?: string;
  icoNumber?: string;
  numberOfBranches?: string;
  numberOfAgents?: string;
  hmrcMlrNumber?: string;
  hmrcRegistrationExpiryDate?: string;
  mlroDetails?: string;
  complianceConsultantDetails?: string;
  accountantDetails?: string;
  itProviderDetails?: string;
  payoutPartnerDetails?: string;
  hasTransactionMonitoring?: string | boolean;
  transactionMonitoringKind?: string;
  hasScreening?: string | boolean;
  screeningKind?: string;
  hasRegulatoryInvestigation?: string | boolean;
  regulatoryInvestigationDetails?: string;
  visitedByAuthorities?: string;
  visitedByAuthoritiesDetails?: string;
  hasOnboardCorporateClients?: string | boolean;
  hasOnboardMsb?: string | boolean;
  hasPaymentsForCharities?: string | boolean;
  hasChangesInManagementOrOwnership?: string | boolean;
  changesInManagementOrOwnershipDetails?: string;
  hasWorkingWithAnyOtherIpsp?: string | boolean;
  workingWithAnyOtherIpspDetails?: string;
  hasAuditedByExternalCompliance?: string | boolean;
  auditedByExternalComplianceDetails?: string;
  contactPersonName?: string;
  phoneNumber?: string;
  faxNumber?: string;
  contactEmail?: string;
  legalEntityIdCode?: string;
  isPepOrAssociate?: string | boolean;
  nameOfPep?: string;
  relToCustomerId?: string;
  relToPep?: string;
  orgEmployerAndPosition?: string;
  positionHeldCountryId?: string;
  politicalFunctionPeriodFrom?: string;
  politicalFunctionPeriodTo?: string;
  abilityMoveGovernmentFunds?: boolean | string;
  abilityMoveGovernmentFundsDetails?: string;
  belongsToGroup?: boolean | string;
  parentCompanyName?: string;
  parentCompanyCountryId?: string;
  subsidiariesInfo?: string;
  mainActivities?: string;
  groupWealthSourceIds?: string[];
  estimatedTotalWealthEuro?: string | number;
  customerAppliesKycProcedures?: string | boolean;
  hasBusinessActivity?: string | boolean;
  businessActivitiesIds?: string[];
  customersMainClientsIds?: string[];
  customersMainClientsOther?: string;
  salesChannelsIds?: string[];
  salesChannelsOther?: string;
  numberOfEmployees?: string | number;
  hasAccountsInOtherFinancialInstitutions?: string | boolean;
  otherFinancialInstitutions?: string;
  sourcesOfFundsIds?: string[];
  sourcesOfFundsLoanBusinessPartner?: string;
  sourcesOfFundsLoanCreditInstitution?: string;
  sourcesOfFundsOther?: string;
  sourceOfWealthIds?: string[];
  sourceOfWealthOther?: string;
  estimatedTotalWealth?: string | number;
  annualIncome?: string | number;
  sourcesOfAnnualIncomes?: {
    sourceOfAnnualIncomeId: string;
    percentage?: string | number;
  }[];
  sourcesOfAnnualIncomesOther?: string;
  accountTypeIds?: string[];
  tradingName?: string;
  questionnairesAccountsCurrencies?: string;
  questionnairesAccountsFlows?: string;
  questionnairesExpectedFundsOrigins?: string;
  groupWealthSourceOther?: string;
}

export interface Questionnarie {
  id: string;
  nationalInsuranceNumberOfDirectors?: string;
  lastThreeYearsAddressHistory?: string;
  criminalConvictedOfAny?: string | boolean;
  criminalReceivedACautionForAny?: string | boolean;
  criminalArrestedOrChargedWithOrBeenInvolved?: string | boolean;
  criminalSubjectToAnyCurrentCriminalInvestigation?: string | boolean;
  criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted?:
    string | boolean;
  criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?:
    string | boolean;
  criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved?:
    string | boolean;
  civilFoundLiableForFraud?: string | boolean;
  civilSubjectToAJudgmentDebt?: string | boolean;
  civilInvolvedInAnyCivilProceedings?: string | boolean;
  civilYouHeldAPositionOfInfluence?: string | boolean;
  civilFiledForBankruptcy?: string | boolean;
  civilSubjectToABankruptcy?: string | boolean;
  businessAndEmploymentDismissedOrAskedTo?: string | boolean;
  businessAndEmploymentDisqualifiedFromActingAsADirector?: string | boolean;
  businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings?:
    string | boolean;
  regulatoryAnAuthorizationFromARegulatoryBodyRefused?: string | boolean;
  regulatoryCensured?: string | boolean;
  regulatoryInvolvedInAnyActivitiesRequiringAuthorization?: string | boolean;
  regulatorySubjectToAnyDisqualification?: string | boolean;
  typeOfBusinessLicence?: string;
  hmrcMlrNumber?: string;
  hmrcRegistrationExpiryDate?: string;
  fcaNumber?: string;
  icoNumber?: string;
  numberOfBranches?: string;
  numberOfAgents?: string;
  mlroDetails?: string;
  complianceConsultantDetails?: string;
  accountantDetails?: string;
  itProviderDetails?: string;
  payoutPartnerDetails?: string;
  hasTransactionMonitoring?: string | boolean;
  transactionMonitoringKind?: string;
  hasScreening?: string | boolean;
  screeningKind?: string;
  hasRegulatoryInvestigation?: string | boolean;
  regulatoryInvestigationDetails?: string;
  visitedByAuthorities?: string;
  visitedByAuthoritiesDetails?: string;
  hasOnboardCorporateClients?: string | boolean;
  hasOnboardMsb?: string | boolean;
  hasPaymentsForCharities?: string | boolean;
  hasChangesInManagementOrOwnership?: string | boolean;
  changesInManagementOrOwnershipDetails?: string;
  hasWorkingWithAnyOtherIpsp?: string | boolean;
  workingWithAnyOtherIpspDetails?: string;
  hasAuditedByExternalCompliance?: string | boolean;
  auditedByExternalComplianceDetails?: string;
  contactPersonName?: string;
  phoneNumber?: string;
  faxNumber?: string;
  contactEmail?: string;
  legalEntityIdCode?: string;
  isPepOrAssociate?: string | boolean;
  nameOfPep?: string;
  relToCustomerId?: string;
  relToPep?: string;
  orgEmployerAndPosition?: string;
  positionHeldCountryId?: string;
  politicalFunctionPeriodFrom?: string;
  politicalFunctionPeriodTo?: string;
  abilityMoveGovernmentFunds?: boolean | string;
  abilityMoveGovernmentFundsDetails?: string;
  belongsToGroup?: boolean | string;
  parentCompanyName?: string;
  parentCompanyCountryId?: string;
  subsidiariesInfo?: string;
  mainActivities?: string;
  groupWealthSourceIds?: string[];
  estimatedTotalWealthEuro?: string | number;
  customerAppliesKycProcedures?: string | boolean;
  hasBusinessActivity?: string | boolean;
  businessActivitiesIds?: string[];
  customersMainClientsIds?: string[];
  customersMainClientsOther?: string;
  salesChannelsIds?: string[];
  salesChannelsOther?: string;
  numberOfEmployees?: string | number;
  hasAccountsInOtherFinancialInstitutions?: string | boolean;
  otherFinancialInstitutions?: string;
  sourcesOfFundsIds?: string[];
  sourcesOfFundsLoanBusinessPartner?: string;
  sourcesOfFundsLoanCreditInstitution?: string;
  sourcesOfFundsOther?: string;
  sourceOfWealthIds?: string[];
  sourceOfWealthOther?: string;
  estimatedTotalWealth?: string | number;
  annualIncome?: string | number;
  sourcesOfAnnualIncomes?:
    | {
        sourceOfAnnualIncomeId: string;
        percentage?: string | number;
      }[]
    | string;
  sourcesOfAnnualIncomesOther?: string;
  accountTypeIds?: string[];
  tradingName?: string;
  questionnairesAccountsCurrencies?: string[];
  questionnairesAccountsFlows?: string[];
  questionnairesExpectedFundsOrigins?: string[];
  questionnairesAffiliatedCompanies?: string[];
  otherNationalitiesIds?: string[];
  maritalStatus?: string;
  spouseFullName?: string;
  natureOfBusinessActivity?: string;
  nameOfCompany?: string;
  ownership?: number | string;
  requiresSourcesOfIncomes?: string | boolean;
  sourcesOfIncomes?:
    | {
        sourceId: string;
        value: string | number;
      }[]
    | string;
  sourcesOfIncomesOther?: string;
  sourcesOfIncomesBorrowedOther?: string;
  requiresSourceAndSizeOfWealth?: string | boolean;
  sourcesAndSizesOfWealth?:
    | {
        id?: string;
        sourceId: string;
        value: string | number;
      }[]
    | string;
  sourcesAndSizesOfWealthOther?: string;
  sourcesAndSizesOfWealthBorrowedOther?: string;
  requiresNetWorth?: string | boolean;
  netWorthAmount?: string | number;
  groupWealthSourceOther?: string;
  secondPassportExpiryDate?: string;
  secondPassportIssueCountryId?: string;
  secondPassportIssueDate?: string;
  secondPassportNumber?: string;
  isUSCitizen?: string | boolean;
  questionnairesCrsFatcaDeclarations?: string;
  homePhoneNumber?: string;
  emails?: string[];
  hasAffiliatedCompanies?: string | boolean;
}

export enum CurrencyType {
  ALL = 'ALL',
  NONE = 'NONE',
  SPECIFIC = 'SPECIFIC',
}

export enum ConnectionTypeEnum {
  CSM = 'CSM',
  VIRTUAL_IBAN = 'VIRTUAL_IBAN',
  NO_IBAN = 'NO_IBAN',
}

export enum PspPartnerTypeEnum {
  CREDIT = 'CREDIT',
  PAYMENT = 'PAYMENT',
  EMONEY = 'EMONEY',
}

export enum PspPartnerLocationEnum {
  DOMESTIC = 'DOMESTIC',
  FOREIGN = 'FOREIGN',
}

export enum ExportFileTypeEnum {
  HARBOUR_HILLS_B2B_LITE = 'HARBOUR_HILLS_B2B_LITE',
  SPITAMEN = 'SPITAMEN',
}

export interface ConnectionType {
  pspIntegrationPaymentId: string;
  connectionType: ConnectionTypeEnum;
  id: string;
}

export interface PspIntergrationCurrency {
  pspIntegrationPaymentId: string;
  currencyId: string;
  currency: Currency;
  id: string;
}

export interface ExternalPspPartner {
  partnerType: PspPartnerTypeEnum;
  location: PspPartnerLocationEnum;
  paymentsTypes: string[];
  companyName: string;
  legalName: string;
  swift?: string;
  sortCode?: string;
  pspIntegrationId?: string;
  caspId?: string;
  pspIntegration?: {
    name: string;
    pspIntegrationPayments: PspIntegrationPayment[];
    id: string;
    externalCustomersSupported?: boolean;
  };
  address: string;
  managerContacts?: string;
  onlineBankLink?: string;
  description?: string;
  active: boolean;
  visibleToClients: boolean;
  countryId: string;
  country: Country;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
  accountOfCorrespondentWithIntermediaryBank?: string;
  beneficiary?: string;
  exportFileType?: ExportFileTypeEnum;
  id: string;
}

export interface PaymentsPartnersSetting {
  pspIntegrationPaymentId: string;
  pspIntegrationPayment: PspIntegrationPayment;
  pspPartnerId: string;
  pspPartner: ExternalPspPartner;
  bic?: string;
  sortCode?: string;
  id: string;
}

export interface PspIntegrationPayment {
  pspIntegrationId: string;
  paymentsTypes: string[];
  currencyType: CurrencyType;
  connectionsTypes: ConnectionType[];
  currencies: PspIntergrationCurrency[];
  paymentsPartnersSettings: PaymentsPartnersSetting[];
  id: string;
}

export interface ExternalAccount {
  account: Account & { organization: { type: string } };
  accountId: string;
  iban: string;
  id: string;
  pspIntegrationConnectionId: string;
  pspIntegrationPayment?: PspIntegrationPayment;
  pspIntegrationPaymentId?: string;
  pspPartner?: PspPartner;
  bank?: string;
  currencyId?: string;
  paymentType?: string;
  accountNumber?: string;
  accountName?: string;
  accountType?: string;
  active?: boolean;
  createdAt?: string;
  createType?: string;
  caspId?: string;
  url?: string;
  currenciesIds?: string[];
  pspIntegrationPaymentAccountsCount?: number;
  render?: Record<string, React.JSX.Element>;
}

export interface ExternalAccountFormValues {
  pspPartnerId: string;
  accountId: string;
  iban: string;
  paymentType: string;
  currencyId: string[];
  status?: string;
  active: boolean;
  caspId?: string;
  casp?: CASP[];
  currenciesIds: string[];
}

export interface DocumentsCategoryState {
  startCursor: string;
  endCursor: string;
  hasNext: boolean;
  hasPrev: boolean;
  prevCursor?: string;
  prevStartCursor?: string;
  prevEndCursor?: string;
  documents?: CustomerDocument[];
}

export type DocumentsState = Record<string, DocumentsCategoryState[]>;

export interface MarginAmount {
  defaultMarginId?: string;
  marginId?: string;
  fromAmount: number;
  feePercent: number;
  id?: string;
}
export interface CustomMargin {
  rateSourceId: string;
  defaultMarginId: string;
  currencyFromId: string;
  riskScoreRangeId?: string;
  organizationId?: string;
  url?: string;
  currencyFrom: {
    name: string;
  };
  currencyToId: string;
  currencyTo: {
    name: string;
  };
  tariffId: string;
  tariff: {
    name: string;
    riskScoreRangeId: string;
  };
  isCustom: boolean;
  targetAccountGainsId: string;
  targetAccountLossesId: string;
  rateSource: {
    name: string;
    description: string;
    id: string;
  };
  id: string;
  amounts?: {
    edges: {
      node: MarginAmount;
    }[];
  };
  render?: Record<string, React.JSX.Element>;
}

export interface RateSource {
  name: string;
  description: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
}

export interface MarginFormValues {
  targetAccountGainsId: string;
  targetAccountLossesId: string;
  rateSourceId?: string;
  amounts: MarginAmount[];
  currencyFromId?: string;
  currencyToId?: string;
  tariffId?: string | null;
  organizationId?: string;
  defaultMarginId?: string;
}

export interface Client {
  id: string;
  email?: string;
  phone: string;
  url?: string;
  status: 'ACTIVE' | 'DISABLED';
  profile?: {
    firstName: string;
    lastName: string;
  };
  totpEnabled?: boolean;
}

export interface ExternalAccountWithIntegrationFormValues {
  accountsIds: string[];
  integrationId: string;
  currenciesIds: string[];
  caspId?: string;
}

export interface PspIntegration {
  id: string;
  name: string;
  externalCustomersSupported: boolean;
  pspIntegrationPayments: {
    id: string;
    currencies: {
      id: string;
      currency: {
        id: string;
        name: string;
      };
    }[];
  }[];
}

export interface PspIntegrationOrganization {
  externalId: string;
  name: string;
  organizationType: OrganizationType;
}

export interface ExternalOrganization {
  organizationId: string;
  organizationType: OrganizationType;
  externalId: string;
  active: boolean;
  id: string;
  createdAt: string;
}

export interface ExternalOrganizationFormValues {
  organizationId: string;
  organizationType: OrganizationType;
  externalId: string;
  active: boolean;
  pspPartnerId: string;
}

export interface ExternalAccountToClientAccountFormValues {
  pspPartnerId: string;
  accountId: string;
  externalId: string;
  caspId?: string;
}

export interface ExternalOrganizationAccount {
  currencies: string[];
  id: string;
  externalId: string;
}

export interface ExternalAccountTopUpOption {
  accountNumber: string;
  bankAccountHolderName: string;
  bankAddress: string;
  bankCountry: string;
  bankName: string;
  bic: string;
  createdAt: string;
  currencyId: string;
  iban: string;
  id: string;
  paymentType?: string;
  pspIntegrationAccountId: string;
  routingCodeEntries?: {
    id?: string;
    routingCodeKey: string;
    routingCodeValue: string;
  }[];
  thirdPartyIntegrationId?: string;
  thirdPartyReference?: string;
}

export interface LinkedClient {
  active: boolean;
  createdAt: string;
  externalId: string;
  id: string;
  organizationId: string;
  organization: {
    firstName?: string;
    lastName?: string;
    companyName?: string;
    id: string;
  };
  organizationType: string;
  pspPartnerId?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface ExternalAccountTopUpOptionFormValues {
  id?: string;
  paymentType: string;
  currencyId: string;
  bankAccountHolderName: string;
  accountNumber: string;
  bankAddress: string;
  bic: string;
  iban: string;
  bankName: string;
  thirdPartyReference?: string;
  pspIntegrationAccountId?: string;
  routingCodeEntries?:
    | {
        id?: string;
        routingCodeKey: string;
        routingCodeValue: string;
      }[]
    | null;
}

export enum RoutingCodeKeyEnum {
  ABA = 'ABA',
  BLZ_CODE = 'BLZ_CODE',
  BRANCH_CODE = 'BRANCH_CODE',
  BSB_CODE = 'BSB_CODE',
  SORT_CODE = 'SORT_CODE',
  IFS_CODE = 'IFS_CODE',
  CLABE_CODE = 'CLABE_CODE',
  CTN = 'CTN',
}
